import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { buyerBudgets } from '@prisma/client';
import buyerBudgetsService, { BuyerBudgetData } from '../../api/services/buyerBudgets';
import { AppDispatch, RootState } from '../store';

interface BuyerBudgetsState {
  budget: buyerBudgets | null;
  loading: boolean;
  error: string | null;
}

const initialState: BuyerBudgetsState = {
  budget: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchBuyerBudget = createAsyncThunk(
  'buyerBudgets/fetch',
  async () => {
    const budget = await buyerBudgetsService.find();
    return budget;
  }
);

export const saveBuyerBudget = createAsyncThunk(
  'buyerBudgets/save',
  async (data: BuyerBudgetData) => {
    const budget = await buyerBudgetsService.save(data);
    return budget;
  }
);

export const updateBuyerBudget = createAsyncThunk(
  'buyerBudgets/update',
  async ({ id, data }: { id: string; data: Partial<BuyerBudgetData> }) => {
    const budget = await buyerBudgetsService.update(id, data);
    return budget;
  }
);

const buyerBudgetsSlice = createSlice({
  name: 'buyerBudgets',
  initialState,
  reducers: {
    clearBuyerBudget: (state) => {
      state.budget = null;
      state.error = null;
    },
    setBuyerBudget: (state, action: PayloadAction<buyerBudgets | null>) => {
      state.budget = action.payload;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch budget
      .addCase(fetchBuyerBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBuyerBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.budget = action.payload;
      })
      .addCase(fetchBuyerBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch budget';
      })
      // Save budget
      .addCase(saveBuyerBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveBuyerBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.budget = action.payload;
      })
      .addCase(saveBuyerBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to save budget';
      })
      // Update budget
      .addCase(updateBuyerBudget.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBuyerBudget.fulfilled, (state, action) => {
        state.loading = false;
        state.budget = action.payload;
      })
      .addCase(updateBuyerBudget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update budget';
      });
  },
});

export const { clearBuyerBudget, setBuyerBudget } = buyerBudgetsSlice.actions;
export default buyerBudgetsSlice.reducer;